import type { Client } from '~/types/client.type'
import type { ClipboardItemAnyResource, ClipboardItemResource, ClipboardItemResourceType } from '~/types/clipboard.type'
import type { Project } from '~/types/project.type'
import type { Task } from '~/types/task.type'
import type { TaskList } from '~/types/taskList.type'
import { copyFeatureNotSupportedHint } from '~/constants'

export const useClipboardStore = defineStore('clipboard', () => {
  const toast = useToast()

  const client = shallowRef<Client>()
  const project = shallowRef<Project<undefined>>()
  const taskList = shallowRef<TaskList<'withProject'>>()
  const task = shallowRef<Task<'withTaskList'>>()

  const clipboardItems = ref(useClipboardItems({ read: false }))

  const isSupported = computed(() => {
    return clipboardItems.value.isSupported && typeof navigator !== 'undefined'
      && !!navigator.clipboard
      && typeof navigator.clipboard.writeText === 'function'
      && typeof navigator.clipboard.write === 'function'
      && typeof navigator.clipboard.readText === 'function'
      && typeof navigator.clipboard.read === 'function'
      && typeof window !== 'undefined'
      && window.isSecureContext
      && typeof ClipboardItem !== 'undefined'
  })

  const didSuccessfullyWriteToClipboard = ref<boolean | undefined>()

  function checkClipboardWritePermission() {
    if (!isSupported.value) {
      didSuccessfullyWriteToClipboard.value = false
      toast.add({ color: 'error', description: copyFeatureNotSupportedHint })
      return false
    }
    return true
  }

  const didSuccessfullyReadFromClipboard = ref<boolean | undefined>()

  const permissionRead = usePermission('clipboard-read')

  const canReadFromClipboard = computed(() => {
    if (!isSupported.value)
      return false

    if (permissionRead.value === 'granted')
      return true

    return permissionRead.value === 'prompt' && (didSuccessfullyReadFromClipboard.value === undefined || didSuccessfullyReadFromClipboard.value)
  })

  watch(canReadFromClipboard, canRead => clipboardItems.value = useClipboardItems({ read: canRead }), { immediate: true })

  function copy(items: ClipboardItems) {
    if (!checkClipboardWritePermission()) {
      return
    }

    try {
      clipboardItems.value.copy(items)
      didSuccessfullyWriteToClipboard.value = true
    }
    catch {
      didSuccessfullyWriteToClipboard.value = false
    }
  }

  const permissionWrite = usePermission('clipboard-write')
  const canWriteToClipboard = computed(() => {
    if (!isSupported.value)
      return false

    if (permissionWrite.value === 'granted')
      return true

    return permissionWrite.value === 'prompt' && (didSuccessfullyWriteToClipboard.value === undefined || didSuccessfullyWriteToClipboard.value)
  })

  const clipboardContent = ref<ClipboardItems>([])
  const hasSeenClipboardPermissionWarning = ref(false)

  syncRef(clipboardContent, computed(() => clipboardItems.value.content), { direction: 'rtl' })

  const { resolve } = useRouter()

  const titleHtml = `:track-it: trackIT`
  const separatorHtml = '--------------------------------------------------------------------------'

  function getProjectHtml(project?: Project<undefined>) {
    if (!project)
      return ''

    const link = resolve({
      name: 'projects-id-view',
      params: {
        view: 'tasks',
        id: project.id,
      },
    })

    const projectHref = new URL(link.href, window.location.origin).href

    return `💼 Projekt: <a href="${projectHref}">${project.name}</a>`
  }

  function getTaskListHtml(taskList: TaskList<undefined>) {
    const link = resolve({
      name: 'task-lists-id',
      params: {
        id: taskList.id,
      },
    })

    const taskListHref = new URL(link.href, window.location.origin).href
    return `📋 Aufgabenliste: <a href="${taskListHref}">${taskList.name}</a>`
  }

  function getTaskHtml(task: Task<undefined>) {
    const link = resolve({
      name: 'tasks-id',
      params: {
        id: task.id,
      },
    })

    const taskHref = new URL(link.href, window.location.origin).href
    return `🕒 Aufgabe: <a href="${taskHref}">${task.name}</a>`
  }

  function getClientHtml(client: Client) {
    const clientLink = resolve({
      name: 'clients-id-view',
      params: {
        view: 'projects',
        id: client.id,
      },
    })

    const clientHref = new URL(clientLink.href, window.location.origin).href

    return `👤 Kunde: <a href="${clientHref}">${client.name}</a>`
  }

  function getLinkHtml(link: string) {
    return `🔗 <a href="${link}">${link}</a>`
  }

  function getJsonResource(resource: ClipboardItemResource['resource'], type: ClipboardItemResourceType) {
    return JSON.stringify({
      resource,
      type,
    })
  }

  function copyClient(clientToCopy: Client) {
    if (!checkClipboardWritePermission()) {
      return
    }

    const link = resolve({
      name: 'clients-id-view',
      params: {
        view: 'projects',
        id: clientToCopy.id,
      },
    })

    const clientHref = new URL(link.href, window.location.origin).href

    const clipboardItems: Record<string, Blob> = {}

    if (ClipboardItem.supports('text/plain'))
      clipboardItems['text/plain'] = new Blob([clientHref], { type: 'text/plain' })

    if (ClipboardItem.supports('web application/json'))
      clipboardItems['web application/json'] = new Blob([getJsonResource(clientToCopy, 'client')], { type: 'application/json' })

    if (ClipboardItem.supports('text/html'))
      clipboardItems['text/html'] = new Blob([[titleHtml, separatorHtml, getClientHtml(clientToCopy), getLinkHtml(clientHref), separatorHtml].join('<br>')], { type: 'text/html' })

    copy([
      new ClipboardItem(clipboardItems),
    ])

    toast.add({ color: 'success', title: 'Kunde kopiert!', description: 'Der Kunde kann nun geteilt werden.' })
  }

  function isClientCopied(clientId: Client['id']) {
    return computed(() => clientId === client.value?.id && clipboardItems.value.copied)
  }

  function copyProject(projectToCopy: Project<undefined>) {
    if (!checkClipboardWritePermission()) {
      return
    }

    const link = resolve({
      name: 'projects-id-view',
      params: {
        view: 'tasks',
        id: projectToCopy.id,
      },
    })

    const projectHref = new URL(link.href, window.location.origin).href

    const clipboardItems: Record<string, Blob> = {}

    if (ClipboardItem.supports('text/plain'))
      clipboardItems['text/plain'] = new Blob([projectHref], { type: 'text/plain' })

    if (ClipboardItem.supports('web application/json'))
      clipboardItems['web application/json'] = new Blob([getJsonResource(projectToCopy, 'project')], { type: 'application/json' })

    if (ClipboardItem.supports('text/html'))
      clipboardItems['text/html'] = new Blob([[titleHtml, separatorHtml, getProjectHtml(projectToCopy), getLinkHtml(projectHref), separatorHtml].join('<br>')], { type: 'text/html' })

    copy([
      new ClipboardItem(clipboardItems),
    ])

    toast.add({ color: 'success', title: 'Projekt kopiert!', description: 'Füge es bei der Zeiteintragserstellung ein oder verschicke es als Link.' })
  }

  function isProjectCopied(projectId: Project['id']) {
    return computed(() => projectId === project.value?.id && clipboardItems.value.copied)
  }

  function copyTaskList(taskListToCopy: TaskList<'withProject'>) {
    if (!checkClipboardWritePermission()) {
      return
    }

    const link = resolve({
      name: 'task-lists-id',
      params: {
        id: taskListToCopy.id,
      },
    })

    const taskListHref = new URL(link.href, window.location.origin).href

    const clipboardItems: Record<string, Blob> = {}

    if (ClipboardItem.supports('text/plain'))
      clipboardItems['text/plain'] = new Blob([taskListHref], { type: 'text/plain' })

    if (ClipboardItem.supports('web application/json'))
      clipboardItems['web application/json'] = new Blob([getJsonResource(taskListToCopy, 'taskList')], { type: 'application/json' })

    if (ClipboardItem.supports('text/html'))
      clipboardItems['text/html'] = new Blob([[titleHtml, separatorHtml, getTaskListHtml(taskListToCopy), getProjectHtml(taskListToCopy.project), getLinkHtml(taskListHref), separatorHtml].join('<br>')], { type: 'text/html' })

    copy([
      new ClipboardItem(clipboardItems),
    ])

    toast.add({ color: 'success', title: 'Aufgabenliste kopiert!', description: 'Die Aufgabenliste kann nun geteilt werden.' })
  }

  function isTaskListCopied(taskListId: TaskList['id']) {
    return computed(() => taskListId === taskList.value?.id && clipboardItems.value.copied)
  }

  function copyTask(taskToCopy: Task<'withTaskList'>) {
    if (!checkClipboardWritePermission()) {
      return
    }

    const link = resolve({
      name: 'tasks-id',
      params: {
        id: taskToCopy.id,
      },
    })

    const taskHref = new URL(link.href, window.location.origin).href

    const clipboardItems: Record<string, Blob> = {}

    if (ClipboardItem.supports('text/plain'))
      clipboardItems['text/plain'] = new Blob([taskHref], { type: 'text/plain' })

    if (ClipboardItem.supports('web application/json'))
      clipboardItems['web application/json'] = new Blob([getJsonResource(taskToCopy, 'task')], { type: 'application/json' })

    if (ClipboardItem.supports('text/html'))
      clipboardItems['text/html'] = new Blob([[titleHtml, separatorHtml, getTaskHtml(taskToCopy), getTaskListHtml(taskToCopy.taskList), getProjectHtml(taskToCopy.taskList.project), getLinkHtml(taskHref), separatorHtml].join('<br>')], { type: 'text/html' })

    copy([
      new ClipboardItem(clipboardItems),
    ])

    toast.add({ color: 'success', title: 'Aufgabe kopiert!', description: 'Füge sie bei der Zeiteintragserstellung ein oder verschicke sie als Link.' })
  }

  function isTaskCopied(taskId: Task['id']) {
    return computed(() => taskId === task.value?.id && clipboardItems.value.copied)
  }

  const clientIdToFetch = ref(0)
  const { data: clientData, execute: fetchClient } = useApiClientGetById(clientIdToFetch, undefined, { immediate: false, watch: false })

  const projectIdToFetch = ref(0)
  const { data: projectData, execute: fetchProject } = useApiProjectGetById(projectIdToFetch, undefined, { immediate: false, watch: false })

  const taskListIdToFetch = ref(0)
  const { data: taskListData, execute: fetchTaskList } = useApiTaskListGetById(taskListIdToFetch, { withProject: true }, { immediate: false, watch: false })

  const taskIdToFetch = ref(0)
  const { data: taskData, execute: fetchTask } = useApiTaskGetById(taskIdToFetch, { withTaskList: true }, { immediate: false, watch: false })

  function clearCopiedResource() {
    client.value = undefined
    project.value = undefined
    taskList.value = undefined
    task.value = undefined
  }

  watch(clipboardContent, async (content) => {
    if (!content.length) {
      clearCopiedResource()
      return
    }

    const newestClipboardItem = content[0]

    if (newestClipboardItem.types.includes('web application/json')) {
      const blob = await newestClipboardItem.getType('web application/json')

      if (!blob)
        return

      const resource = JSON.parse(await blob.text()) as ClipboardItemAnyResource

      if (!('type' in resource) || !/^project$|^task$|^client$|^taskList$/.test(resource.type))
        return

      clearCopiedResource()

      switch (resource.type) {
        case 'project':
          project.value = resource.resource
          break
        case 'client':
          client.value = resource.resource
          break
        case 'task':
          task.value = resource.resource
          break
        case 'taskList':
          taskList.value = resource.resource
          break
      }
    }
    else if (newestClipboardItem.types.includes('text/plain')) {
      clearCopiedResource()

      const blob = await newestClipboardItem.getType('text/plain')

      if (!blob)
        return

      const text = await blob.text()

      if (!text.startsWith(window.location.origin))
        return

      const route = resolve({
        path: text.replace(new RegExp(window.location.origin), ''),
      })

      switch (route.name) {
        case 'clients-id-view':
        {
          const clientId = Number.parseInt(route.params.id)
          if (!Number.isNaN(clientId)) {
            clientIdToFetch.value = clientId
            await fetchClient()
            if (clientData.value)
              client.value = clientData.value.data
          }
          break
        }
        case 'projects-id-view':
        {
          const projectId = Number.parseInt(route.params.id)
          if (!Number.isNaN(projectId)) {
            projectIdToFetch.value = projectId
            await fetchProject()
            if (projectData.value)
              project.value = projectData.value.data
          }
          break
        }
        case 'task-lists-id':
        {
          const taskListId = Number.parseInt(route.params.id)
          if (!Number.isNaN(taskListId)) {
            taskListIdToFetch.value = taskListId
            await fetchTaskList()
            if (taskListData.value)
              taskList.value = taskListData.value.data
          }
          break
        }
        case 'tasks-id':
        {
          const taskId = Number.parseInt(route.params.id)
          if (!Number.isNaN(taskId)) {
            taskIdToFetch.value = taskId
            await fetchTask()
            if (taskData.value)
              task.value = taskData.value.data
          }
          break
        }
        default:
          break
      }
    }
  }, { immediate: true })

  async function requestClipboardReadPermission() {
    if (!navigator?.userActivation.isActive)
      return

    if (!isSupported.value) {
      didSuccessfullyReadFromClipboard.value = false
      return
    }

    try {
      clipboardContent.value = await navigator?.clipboard.read()
      didSuccessfullyReadFromClipboard.value = true
    }
    // eslint-disable-next-line unused-imports/no-unused-vars
    catch (_err) {
      didSuccessfullyReadFromClipboard.value = false
      if (hasSeenClipboardPermissionWarning.value)
        return
      hasSeenClipboardPermissionWarning.value = true
      toast.add({
        title: 'Zwischenablage-Leseberechtigung prüfen!',
        duration: 10000,
        description: 'Bitte Zwischenablage-Leseberechtigung prüfen (siehe 📋-Symbol in der Adressleiste des Browsers). Der Webseite wurde das Lesen der Zwischenablage nicht genehmigt. Das Einfügen von Projekten/Aufgaben wird nicht funktionieren.',
      })
    }
  }

  const isAnyResourceCopied = computed(() => clipboardItems.value.copied)

  return {
    isAnyResourceCopied,
    isClientCopied,
    isProjectCopied,
    isTaskListCopied,
    isTaskCopied,
    client,
    project,
    taskList,
    task,
    copyClient,
    copyProject,
    copyTaskList,
    copyTask,
    clipboardContent,
    requestClipboardReadPermission,
    canWriteToClipboard,
    canReadFromClipboard,
  }
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useClipboardStore, import.meta.hot))
